import { createContext, useContext, useEffect, useMemo, FC, PropsWithChildren } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import useAuthStore, { User, AuthState } from '../stores/useAuthStore';

// Define the context type
type AuthContextType = {
  isAuthenticated: boolean;
  isMfaRequired: boolean;
  isLoading: boolean;
  authState: AuthState;
  user: User | null;
  login: (email: string, password: string) => Promise<boolean>;
  verifyMfa: (code: string) => Promise<boolean>;
  verifyMfaCode: (code: string) => Promise<boolean>;
  logout: () => void;
};

// Create the context with a default null value
const AuthContext = createContext<AuthContextType | null>(null);

// Provider component
export const AuthProvider: FC<PropsWithChildren> = ({ children }) => {
  const {
    authState,
    isLoading,
    user,
    isAuthenticated,
    isMfaRequired,
    login,
    verifyMfa,
    logout,
  } = useAuthStore();

  const navigate = useNavigate();
  const location = useLocation();

  // Handle authentication state changes
  useEffect(() => {
    const publicPaths = ['/blockkit','/elements','/login', '/forgot-password', '/reset-password', '/mfa-verify', '/session'];
    const isPublicPath = publicPaths.some(path => location.pathname.startsWith(path));

    const isUserAuthenticated = isAuthenticated();

    if (isUserAuthenticated && user?.role) {
      const userRole = user.role;
      const redirectPath =
        userRole === 'admin' ? '/admin' :
        userRole === 'proctor' ? '/proctor' :
        userRole === 'testTaker' ? '/test-taker' :
        userRole === 'faculty' ? '/faculty' :
        '/';

      if (location.pathname === '/login' ||
          (location.pathname === '/' && !isPublicPath)) {
        navigate(redirectPath, { replace: true });
      }
    } else if (location.pathname.startsWith('/blockkit')) {
      // do nothing
    } else if (!isUserAuthenticated && !isPublicPath) {
      console.log('User is not authenticated and not on a public path', location.pathname);
      navigate('/login', { replace: true });
    }
  }, [authState, navigate, location.pathname, user, isAuthenticated]);

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo<AuthContextType>(() => ({
    isAuthenticated: isAuthenticated(),
    isMfaRequired: isMfaRequired(),
    isLoading,
    authState,
    user,
    login,
    verifyMfa,
    verifyMfaCode: verifyMfa, // Alias for backward compatibility
    logout,
  }), [isLoading, authState, user, login, verifyMfa, logout, isAuthenticated, isMfaRequired]);

  // @ts-ignore - Workaround for React 18 types
  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

// Custom hook to use the auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
